import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosConfig';
import SearchableDropdown from './SearchableDropdown';
import QuickAddModal from './QuickAddModal_Dropdown';
import '../styles/nassar-theme.css';
import './AddClearanceFileModal.css';

const EditCustomsTariffsModal = ({ isOpen, onClose, onSuccess, tariffData }) => {
    const [formData, setFormData] = useState({
        clearance_file_num: '',
        origin_country: ''
    });

    const [tariffItems, setTariffItems] = useState([]);
    const [existingItemIds, setExistingItemIds] = useState([]);

    const [lookupData, setLookupData] = useState({
        origin_countries: [],
        custom_tariffs: []
    });

    const [quickAddModal, setQuickAddModal] = useState({
        isOpen: false,
        type: '',
        title: '',
        fieldLabel: '',
        endpoint: '',
        fields: null,
        editMode: false,
        editId: null,
        targetItemIndex: null
    });

    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deleteModal, setDeleteModal] = useState({
        isOpen: false,
        item: null,
        endpoint: '',
        hasError: false
    });
    
    // Remove tariff item confirmation modal state
    const [removeTariffModal, setRemoveTariffModal] = useState({
        isOpen: false,
        tariffIndex: null,
        tariffName: ''
    });

    useEffect(() => {
        if (isOpen) {
            fetchLookupData();
        }
    }, [isOpen]);

    // Pre-fill form with tariffData when modal opens
    useEffect(() => {
        if (isOpen && tariffData) {
            setFormData({
                clearance_file_num: tariffData.clearance_file_num || '',
                origin_country: tariffData.origin_country || ''
            });

            // Load existing tariff list items
            if (tariffData.tariffs_list && tariffData.tariffs_list.length > 0) {
                const items = tariffData.tariffs_list.map(item => ({
                    id: item.id,
                    custom_tariff: item.custom_tariff || '',
                    desc_item_ar: item.desc_item_ar || '',
                    desc_item_en: item.desc_item_en || '',
                    note: item.note || ''
                }));
                setTariffItems(items);
                setExistingItemIds(items.map(i => i.id));
            } else {
                setTariffItems([]);
                setExistingItemIds([]);
            }
        }
    }, [isOpen, tariffData]);

    const fetchLookupData = async () => {
        try {
            const [originCountries, customTariffs] = await Promise.all([
                axiosInstance.get('/origin-countries-ct/'),
                axiosInstance.get('/custom-tariffs/')
            ]);

            const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '', 'ar');

            setLookupData({
                origin_countries: originCountries.data.sort(sortByName),
                custom_tariffs: customTariffs.data.sort(sortByName)
            });
        } catch (err) {
            console.error('Error fetching lookup data:', err);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    const handleDropdownChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: false }));
        }
    };

    // Tariff items management
    const addTariffItem = () => {
        setTariffItems([...tariffItems, { custom_tariff: '', desc_item_ar: '', desc_item_en: '', note: '' }]);
    };

    const removeTariffItem = (index) => {
        const item = tariffItems[index];
        // If item has an id, it exists in database - show confirmation
        if (item && item.id) {
            const tariffOption = lookupData.custom_tariffs.find(t => t.id === item.custom_tariff);
            setRemoveTariffModal({
                isOpen: true,
                tariffIndex: index,
                tariffName: tariffOption?.name || `التعريفة ${index + 1}`
            });
        } else {
            // New item (not saved yet) - remove immediately
            setTariffItems(tariffItems.filter((_, i) => i !== index));
        }
    };

    const confirmRemoveTariff = () => {
        const { tariffIndex } = removeTariffModal;
        setTariffItems(tariffItems.filter((_, i) => i !== tariffIndex));
        setRemoveTariffModal({ isOpen: false, tariffIndex: null, tariffName: '' });
    };

    const cancelRemoveTariff = () => {
        setRemoveTariffModal({ isOpen: false, tariffIndex: null, tariffName: '' });
    };

    const handleTariffItemChange = (index, field, value) => {
        const updated = [...tariffItems];
        updated[index][field] = value;
        setTariffItems(updated);
        if (error) setError('');
        if (validationErrors[`tariff_${index}_${field}`]) {
            setValidationErrors(prev => ({ ...prev, [`tariff_${index}_${field}`]: false }));
        }
    };

    const openQuickAdd = (type, title, fieldLabel, endpoint, fields = null, targetItemIndex = null) => {
        setQuickAddModal({
            isOpen: true,
            type,
            title,
            fieldLabel,
            endpoint,
            fields,
            editMode: false,
            editId: null,
            targetItemIndex
        });
    };

    const handleEditLookup = (type, title, fieldLabel, endpoint, fields, item) => {
        setQuickAddModal({
            isOpen: true,
            type,
            title: `تعديل ${title.replace('إضافة ', '').replace(' جديدة', '').replace(' جديد', '').replace('جديدة', '').replace('جديد', '').trim()}`,
            fieldLabel,
            endpoint,
            fields,
            editMode: true,
            editId: item.id,
            editData: item
        });
    };

    const handleDeleteLookup = (endpoint, item) => {
        if (endpoint === '/custom-tariffs/') {
            const usedIndex = tariffItems.findIndex(t => t.custom_tariff === item.id);
            if (usedIndex !== -1) {
                setError(`لا يمكن حذف "${item.name}" لأنه مستخدم في التعريفة رقم ${usedIndex + 1}`);
                return;
            }
        }
        if (endpoint === '/origin-countries-ct/' && formData.origin_country === item.id) {
            setError(`لا يمكن حذف "${item.name}" لأنه محدد حالياً في حقل بلد المنشأ`);
            return;
        }
        setDeleteModal({
            isOpen: true,
            item,
            endpoint,
            hasError: false
        });
    };

    const confirmDeleteItem = async () => {
        const { endpoint, item } = deleteModal;
        try {
            await axiosInstance.delete(`${endpoint}${item.id}/`);
            await fetchLookupData();
            closeDeleteModal();
        } catch (err) {
            const errorData = err.response?.data;
            if (errorData?.error) {
                setError(errorData.error);
                setDeleteModal(prev => ({ ...prev, hasError: true }));
            } else {
                setError('حدث خطأ أثناء الحذف');
                setDeleteModal(prev => ({ ...prev, hasError: true }));
            }
        }
    };

    const closeDeleteModal = () => {
        setDeleteModal({ isOpen: false, item: null, endpoint: '', hasError: false });
        setError('');
    };

    const handleQuickAddSave = async (data) => {
        const { endpoint, editMode, editId, targetItemIndex } = quickAddModal;
        
        let response;
        if (editMode && editId) {
            response = await axiosInstance.put(`${endpoint}${editId}/`, data);
        } else {
            response = await axiosInstance.post(endpoint, data);
        }
        
        await fetchLookupData();
        
        if (!editMode && response.data.id) {
            if (endpoint === '/origin-countries-ct/') {
                setFormData(prev => ({ ...prev, origin_country: response.data.id }));
            } else if (endpoint === '/custom-tariffs/' && targetItemIndex !== null) {
                const updated = [...tariffItems];
                updated[targetItemIndex].custom_tariff = response.data.id;
                setTariffItems(updated);
            }
        }
        
        setQuickAddModal({ ...quickAddModal, isOpen: false });
    };

    const validateForm = () => {
        const errors = {};
        let hasErrors = false;

        if (tariffItems.length > 0) {
            tariffItems.forEach((item, index) => {
                if (!item.custom_tariff) {
                    errors[`tariff_${index}_custom_tariff`] = true;
                    hasErrors = true;
                }
            });
        }

        if (hasErrors) {
            setValidationErrors(errors);
            setError('الرجاء ملء جميع الحقول المطلوبة في التعريفات');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setIsLoading(true);
        setError('');

        try {
            // Step 1: Update the parent CustomsTariffs record
            const parentData = {
                clearance_file_num: formData.clearance_file_num || null,
                origin_country: formData.origin_country || null
            };

            await axiosInstance.put(`/customs-tariffs/${tariffData.id}/`, parentData);

            // Step 2: Handle tariff list items
            const currentItemIds = tariffItems.filter(i => i.id).map(i => i.id);
            
            // Delete removed items
            const deletedIds = existingItemIds.filter(id => !currentItemIds.includes(id));
            for (const id of deletedIds) {
                await axiosInstance.delete(`/customs-tariffs-items/${id}/`);
            }

            // Update existing items and create new ones
            for (const item of tariffItems) {
                const itemData = {
                    customs_tariffs: tariffData.id,
                    custom_tariff: item.custom_tariff,
                    desc_item_ar: item.desc_item_ar || null,
                    desc_item_en: item.desc_item_en || null,
                    note: item.note || null
                };

                if (item.id) {
                    // Update existing
                    await axiosInstance.put(`/customs-tariffs-items/${item.id}/`, itemData);
                } else {
                    // Create new
                    await axiosInstance.post('/customs-tariffs-items/', itemData);
                }
            }

            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Error updating tariff:', err);
            setError(err.response?.data?.message || 'حدث خطأ أثناء تحديث التعريفة الجمركية');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteRecord = async () => {
        if (!confirmDelete) {
            setConfirmDelete(true);
            return;
        }

        setIsDeleting(true);
        setError('');

        try {
            await axiosInstance.delete(`/customs-tariffs/${tariffData.id}/`);
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Error deleting tariff:', err);
            setError(err.response?.data?.message || 'حدث خطأ أثناء حذف التعريفة الجمركية');
        } finally {
            setIsDeleting(false);
            setConfirmDelete(false);
        }
    };

    const handleClose = () => {
        setFormData({
            clearance_file_num: '',
            origin_country: ''
        });
        setTariffItems([]);
        setExistingItemIds([]);
        setError('');
        setValidationErrors({});
        setConfirmDelete(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="modal-overlay" onClick={handleClose}>
                <div className="modal modal-large add-file-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h3 className="modal-title">تعديل التعريفة الجمركية - {tariffData?.clearance_file_num || 'بدون رقم ملف'}</h3>
                        <button type="button" className="modal-close" onClick={handleClose}>
                            <svg fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {/* Section 1: معلومات أساسية */}
                            <div className="form-section">
                                <h4 className="section-title">معلومات أساسية</h4>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">
                                            رقم ملف التخليص
                                        </label>
                                        <input
                                            type="text"
                                            name="clearance_file_num"
                                            className="form-input"
                                            value={formData.clearance_file_num}
                                            onChange={handleInputChange}
                                            placeholder="أدخل رقم ملف التخليص (اختياري)"
                                            style={{
                                                borderColor: validationErrors.clearance_file_num ? '#dc3545' : undefined,
                                                borderWidth: validationErrors.clearance_file_num ? '2px' : undefined
                                            }}
                                        />
                                    </div>

                                    <SearchableDropdown
                                        label="بلد المنشأ"
                                        options={lookupData.origin_countries}
                                        value={formData.origin_country}
                                        onChange={(val) => handleDropdownChange('origin_country', val)}
                                        onAddNew={() => openQuickAdd('origin_country', 'إضافة بلد منشأ جديد', 'اسم البلد', '/origin-countries-ct/')}
                                        onEdit={(item) => handleEditLookup('origin_country', 'إضافة بلد منشأ جديد', 'اسم البلد', '/origin-countries-ct/', null, item)}
                                        onDelete={(item) => handleDeleteLookup('/origin-countries-ct/', item)}
                                        addNewLabel="إضافة بلد جديد"
                                        hasError={validationErrors.origin_country}
                                    />
                                </div>
                            </div>

                            {/* Section 2: قائمة التعريفات الجمركية */}
                            <div className="form-section">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tariffItems.length > 0 ? '1rem' : '0' }}>
                                    <h4 className="section-title" style={{ marginBottom: 0 }}>التعريفات الجمركية</h4>
                                    <button 
                                        type="button" 
                                        className="btn btn-accent"
                                        onClick={addTariffItem}
                                        style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                                    >
                                        <svg style={{ width: '18px', height: '18px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>إضافة تعريفة</span>
                                    </button>
                                </div>
                                
                                {tariffItems.length > 0 && tariffItems.map((item, index) => (
                                    <div key={item.id || `new-${index}`} style={{ 
                                        padding: '1.25rem', 
                                        border: '2px solid #e1e8ed', 
                                        borderRadius: '10px', 
                                        marginBottom: '1rem',
                                        backgroundColor: '#fafbfc'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                            <h5 style={{ margin: 0, color: '#03428d', fontWeight: '600' }}>تعريفة {index + 1}</h5>
                                            <button 
                                                type="button"
                                                onClick={() => removeTariffItem(index)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    color: '#dc3545',
                                                    padding: '4px'
                                                }}
                                                title="حذف التعريفة"
                                            >
                                                <svg style={{ width: '20px', height: '20px' }} fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="form-grid">
                                            <SearchableDropdown
                                                label="التعريفة الجمركية"
                                                required={true}
                                                options={lookupData.custom_tariffs}
                                                value={item.custom_tariff}
                                                onChange={(val) => handleTariffItemChange(index, 'custom_tariff', val)}
                                                onAddNew={() => openQuickAdd('custom_tariff', 'إضافة تعريفة جمركية جديدة', 'اسم التعريفة', '/custom-tariffs/', null, index)}
                                                onEdit={(itm) => handleEditLookup('custom_tariff', 'إضافة تعريفة جمركية جديدة', 'اسم التعريفة', '/custom-tariffs/', null, itm)}
                                                onDelete={(itm) => handleDeleteLookup('/custom-tariffs/', itm)}
                                                addNewLabel="إضافة تعريفة جديدة"
                                                hasError={validationErrors[`tariff_${index}_custom_tariff`]}
                                            />
                                            <div className="form-group">
                                                <label className="form-label">ملاحظات</label>
                                                <input
                                                    type="text"
                                                    className="form-input"
                                                    value={item.note}
                                                    onChange={(e) => handleTariffItemChange(index, 'note', e.target.value)}
                                                    placeholder="ملاحظات (اختياري)"
                                                />
                                            </div>
                                        </div>
                                        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: '0.75rem' }}>
                                            <div className="form-group">
                                                <label className="form-label">وصف الأصناف (عربي)</label>
                                                <textarea
                                                    className="form-input"
                                                    value={item.desc_item_ar}
                                                    onChange={(e) => handleTariffItemChange(index, 'desc_item_ar', e.target.value)}
                                                    placeholder="وصف الأصناف بالعربي (اختياري)"
                                                    rows="2"
                                                    style={{ resize: 'vertical', minHeight: '60px' }}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="form-label">وصف الأصناف (إنجليزي)</label>
                                                <textarea
                                                    className="form-input"
                                                    value={item.desc_item_en}
                                                    onChange={(e) => handleTariffItemChange(index, 'desc_item_en', e.target.value)}
                                                    placeholder="Item description in English (optional)"
                                                    rows="2"
                                                    style={{ resize: 'vertical', minHeight: '60px', direction: 'ltr', textAlign: 'left' }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {tariffItems.length === 0 && (
                                    <div style={{ 
                                        textAlign: 'center', 
                                        padding: '2rem', 
                                        color: '#adb5bd',
                                        border: '2px dashed #e1e8ed',
                                        borderRadius: '10px',
                                        marginTop: '1rem'
                                    }}>
                                        <p>لم يتم إضافة أي تعريفة جمركية بعد</p>
                                        <p style={{ fontSize: '0.85rem' }}>اضغط على "إضافة تعريفة" لإضافة تعريفة جمركية</p>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="error-message" style={{ marginTop: '1rem' }}>
                                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button 
                                    type="submit" 
                                    className="btn btn-accent btn-large"
                                    disabled={isLoading || isDeleting}
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                                            <span>جاري الحفظ...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            <span>حفظ التعديلات</span>
                                        </>
                                    )}
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-outline btn-large"
                                    onClick={handleClose}
                                    disabled={isLoading || isDeleting}
                                >
                                    <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    <span>إلغاء</span>
                                </button>
                            </div>
                            <button 
                                type="button" 
                                className="btn btn-large"
                                onClick={handleDeleteRecord}
                                disabled={isLoading || isDeleting}
                                style={{
                                    backgroundColor: confirmDelete ? '#dc3545' : '#fff',
                                    color: confirmDelete ? '#fff' : '#dc3545',
                                    border: '2px solid #dc3545',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
                                        <span>جاري الحذف...</span>
                                    </>
                                ) : (
                                    <>
                                        <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>{confirmDelete ? 'تأكيد الحذف' : 'حذف التعريفة'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* Quick Add Modal */}
            <QuickAddModal
                isOpen={quickAddModal.isOpen}
                onClose={() => setQuickAddModal({ ...quickAddModal, isOpen: false })}
                onSave={handleQuickAddSave}
                title={quickAddModal.title}
                fieldLabel={quickAddModal.fieldLabel}
                fieldName="name"
                placeholder={`أدخل ${quickAddModal.fieldLabel}`}
                fields={quickAddModal.fields}
                editMode={quickAddModal.editMode}
                editData={quickAddModal.editData}
            />

            {/* Remove Tariff Item Confirmation Modal */}
            {removeTariffModal.isOpen && (
                <div className="modal-overlay" onClick={cancelRemoveTariff} style={{ zIndex: 2100 }}>
                    <div className="modal modal-small" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
                        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' }}>
                            <h3 className="modal-title" style={{ color: 'white' }}>تأكيد إزالة التعريفة</h3>
                            <button type="button" className="modal-close" onClick={cancelRemoveTariff} style={{ color: 'white' }}>
                                <svg fill="currentColor" viewBox="0 0 20 20" style={{ width: '24px', height: '24px' }}>
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                                <div style={{
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fee2e2',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1.25rem',
                                    border: '3px solid #dc3545'
                                }}>
                                    <svg style={{ width: '36px', height: '36px', color: '#dc3545' }} fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 style={{ 
                                    fontSize: '1.25rem', 
                                    fontWeight: '700', 
                                    color: '#2c3e50', 
                                    marginBottom: '0.75rem' 
                                }}>
                                    هل أنت متأكد من إزالة هذه التعريفة؟
                                </h3>
                                <p style={{ 
                                    fontSize: '1.1rem', 
                                    color: '#03428d', 
                                    fontWeight: '600',
                                    marginBottom: '0.5rem'
                                }}>
                                    "{removeTariffModal.tariffName}"
                                </p>
                                <p style={{ 
                                    fontSize: '0.9rem', 
                                    color: '#6c757d',
                                    lineHeight: '1.6'
                                }}>
                                    سيتم حذف التعريفة نهائياً عند حفظ التعديلات
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button 
                                type="button" 
                                className="btn"
                                onClick={confirmRemoveTariff}
                                style={{ 
                                    flex: 1,
                                    maxWidth: '150px',
                                    background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    fontWeight: '600'
                                }}
                            >
                                نعم، أزل
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline"
                                onClick={cancelRemoveTariff}
                                style={{ 
                                    flex: 1,
                                    maxWidth: '150px',
                                    background: 'white',
                                    color: '#6c757d',
                                    border: '2px solid #e1e8ed',
                                    padding: '0.75rem 1.5rem',
                                    fontSize: '1rem',
                                    fontWeight: '600'
                                }}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.isOpen && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header" style={{ background: deleteModal.hasError ? 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' : 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' }}>
                            <h3 className="modal-title" style={{ color: 'white' }}>{deleteModal.hasError ? 'تنبيه' : 'تأكيد الحذف'}</h3>
                            <button type="button" className="modal-close" onClick={closeDeleteModal} style={{ color: 'white' }}>
                                <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                                {deleteModal.hasError ? (
                                    <p style={{ fontSize: '1.15rem', color: '#495057', fontWeight: '600', lineHeight: '1.8' }}>{error}</p>
                                ) : (
                                    <>
                                        <p style={{ fontSize: '1.1rem', color: '#2c3e50', marginBottom: '0.5rem', fontWeight: '600' }}>هل أنت متأكد من حذف</p>
                                        <p style={{ fontSize: '1.25rem', color: '#000', fontWeight: '700', marginBottom: '1rem' }}>"{deleteModal.item?.name}"</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            {deleteModal.hasError ? (
                                <button type="button" className="btn" onClick={closeDeleteModal} style={{ flex: 1, maxWidth: '250px', background: 'linear-gradient(135deg, #03428d 0%, #0066cc 100%)', color: 'white', border: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}>فهمت</button>
                            ) : (
                                <>
                                    <button type="button" className="btn" onClick={confirmDeleteItem} style={{ flex: 1, background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', color: 'white', border: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}>حذف</button>
                                    <button type="button" className="btn btn-outline" onClick={closeDeleteModal} style={{ flex: 1, background: 'white', color: '#6c757d', border: '2px solid #e1e8ed', padding: '0.75rem 1.5rem', fontSize: '1rem', fontWeight: '600' }}>إلغاء</button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EditCustomsTariffsModal;
